import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Clock, Gamepad2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * OBS Browser Source Overlay for Challenges - Optimized for 800x600
 */
const OBSOverlay = () => {
  const { challengeId } = useParams();
  const [searchParams] = useSearchParams();
  const [challenge, setChallenge] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsScroll, setNeedsScroll] = useState(false);
  const rulesContainerRef = useRef(null);
  const rulesListRef = useRef(null);

  const theme = searchParams.get('theme') || 'dark';
  const show = searchParams.get('show') || 'all';
  const animate = searchParams.get('animate') !== 'false';

  // Check if rules need scrolling
  const checkOverflow = useCallback(() => {
    if (rulesContainerRef.current && rulesListRef.current) {
      const containerHeight = rulesContainerRef.current.clientHeight;
      const listHeight = rulesListRef.current.scrollHeight;
      setNeedsScroll(listHeight > (containerHeight - 20));
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(checkOverflow, 100);
    window.addEventListener('resize', checkOverflow);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [challenge, checkOverflow]);

  // Theme configurations
  const themeConfig = {
    dark: { 
      accent: '#00ff00',
      accentRgb: '0, 255, 0',
      text: '#ffffff',
      secondary: '#888888',
    },
    neon: {
      accent: '#00ffff',
      accentRgb: '0, 255, 255',
      text: '#ffffff',
      secondary: '#a855f7',
    },
    red: {
      accent: '#ff4444',
      accentRgb: '255, 68, 68',
      text: '#ffffff',
      secondary: '#ff8888',
    },
    gold: {
      accent: '#ffd700',
      accentRgb: '255, 215, 0',
      text: '#ffffff',
      secondary: '#ffaa00',
    }
  };

  const currentTheme = themeConfig[theme] || themeConfig.dark;

  // Fetch challenge data
  useEffect(() => {
    const fetchChallenge = async () => {
      try {
        const response = await axios.get(`${API}/api/challenges`);
        let found = null;
        
        if (challengeId && challengeId !== 'active') {
          found = response.data.find(c => c.id === challengeId);
        } else {
          // Get first active, or first scheduled if no active
          found = response.data.find(c => c.status === 'active') || 
                  response.data.find(c => c.status === 'scheduled');
        }
        
        if (found) {
          setChallenge(found);
          setError(null);
        } else {
          setError('No challenge found');
        }
      } catch (err) {
        setError('Failed to load');
      } finally {
        setLoading(false);
      }
    };

    fetchChallenge();
    const interval = setInterval(fetchChallenge, 30000);
    return () => clearInterval(interval);
  }, [challengeId]);

  // Timer logic - handles both scheduled (start_time) and live (end_time)
  useEffect(() => {
    if (!challenge) return;

    const isLive = challenge.status === 'active';
    const isScheduled = challenge.status === 'scheduled';
    
    // Determine which time to count down to
    const targetTime = isLive ? challenge.end_time : 
                       isScheduled ? challenge.start_time : null;
    
    if (!targetTime) {
      setTimeLeft(null);
      return;
    }

    const updateTimer = () => {
      const target = new Date(targetTime);
      const now = new Date();
      const diff = target - now;

      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true });
        return;
      }

      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
        expired: false
      });
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [challenge]);

  if (loading) {
    return (
      <div className="w-[800px] h-[600px] flex items-center justify-center" style={{ background: 'transparent' }}>
        <div className="animate-pulse text-xl" style={{ color: currentTheme.accent }}>Loading...</div>
      </div>
    );
  }

  if (error || !challenge) {
    return (
      <div className="w-[800px] h-[600px] flex items-center justify-center" style={{ background: 'transparent' }}>
        <div className="text-xl" style={{ color: currentTheme.secondary }}>{error || 'No challenge'}</div>
      </div>
    );
  }

  const showAll = show === 'all';
  const showTimer = show === 'timer' || showAll;
  const showPrize = show === 'prize' || showAll;
  const showTitle = show === 'title' || showAll;
  const isLive = challenge.status === 'active';
  const isScheduled = challenge.status === 'scheduled';
  
  // Get rules based on visibility settings
  const getVisibleRules = () => {
    // If rules_config exists, use visibility settings
    if (challenge.rules_config && challenge.rules_config.length > 0) {
      if (isLive) {
        // Show all rules when live (no blur)
        return challenge.rules_config.map(r => ({
          text: r.text,
          shouldBlur: false
        }));
      } else {
        // For scheduled: show 'always' and 'blur_scheduled', hide 'live_only'
        return challenge.rules_config
          .filter(r => r.visibility === 'always' || r.visibility === 'blur_scheduled')
          .map(r => ({
            text: r.text,
            shouldBlur: r.visibility === 'blur_scheduled'
          }));
      }
    }
    // Fallback to legacy rules string (all visible, no blur)
    return challenge.rules ? challenge.rules.split('\n').filter(r => r.trim()).map(text => ({
      text,
      shouldBlur: false
    })) : [];
  };
  
  const rules = getVisibleRules();
  const hasHiddenRules = isScheduled && challenge.rules_config?.some(r => r.visibility === 'live_only');

  // Timer label based on status
  const timerLabel = isLive ? 'Time Remaining' : isScheduled ? 'Starts In' : '';
  const timerColor = isLive ? currentTheme.accent : '#ffaa00';

  // Determine container size based on what's being shown
  const isCompactMode = show === 'timer' || show === 'prize';
  const containerClass = isCompactMode 
    ? 'inline-block p-2' 
    : 'w-[800px] h-[600px] p-4';

  return (
    <div className={containerClass} style={{ background: 'transparent' }}>
      <div 
        className={`${isCompactMode ? 'inline-flex' : 'w-full h-full flex'} rounded-xl overflow-hidden`}
        style={{
          background: `linear-gradient(135deg, rgba(${currentTheme.accentRgb}, 0.05) 0%, rgba(0,0,0,0.8) 100%)`,
          border: `1px solid rgba(${currentTheme.accentRgb}, 0.25)`,
          boxShadow: `0 0 15px rgba(${currentTheme.accentRgb}, 0.1)`,
        }}
      >
        {/* Left Column - Main Info */}
        <div className={`flex flex-col ${isCompactMode ? 'p-3' : 'flex-1 p-6'}`}>
          
          {/* Header with Game Cover */}
          {showTitle && (
            <div className="flex items-start gap-4 mb-4">
              {challenge.game_cover_url && (
                <img 
                  src={challenge.game_cover_url} 
                  alt={challenge.game_name}
                  className="w-24 h-32 object-cover rounded-xl flex-shrink-0"
                  style={{ 
                    boxShadow: `0 0 15px rgba(${currentTheme.accentRgb}, 0.2)`,
                    border: `1px solid rgba(${currentTheme.accentRgb}, 0.25)`
                  }}
                />
              )}
              
              <div className="flex-1 min-w-0">
                {/* Status Badge */}
                <div className="flex items-center gap-3 mb-2">
                  <span 
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-bold uppercase tracking-wider"
                    style={{
                      background: isLive ? `rgba(${currentTheme.accentRgb}, 0.15)` : 'rgba(255, 170, 0, 0.15)',
                      color: isLive ? currentTheme.accent : '#ffaa00',
                      border: `1px solid ${isLive ? `rgba(${currentTheme.accentRgb}, 0.4)` : 'rgba(255, 170, 0, 0.4)'}`
                    }}
                  >
                    <span 
                      className={`w-2 h-2 rounded-full ${animate && isLive ? 'animate-pulse' : ''}`}
                      style={{ background: isLive ? currentTheme.accent : '#ffaa00' }}
                    />
                    {isLive ? 'LIVE' : 'COMING SOON'}
                  </span>
                </div>
                
                {/* Challenge Title */}
                <h1 
                  className="text-3xl font-black tracking-tight mb-2 leading-tight"
                  style={{ 
                    color: currentTheme.text,
                    textShadow: `0 0 20px rgba(${currentTheme.accentRgb}, 0.3)`
                  }}
                >
                  {challenge.title}
                </h1>
                
                {/* Game Name */}
                {challenge.game_name && (
                  <div className="flex items-center gap-2 text-base" style={{ color: currentTheme.secondary }}>
                    <Gamepad2 className="w-5 h-5" />
                    <span>{challenge.game_name}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Prize Section */}
          {showPrize && (
            <div 
              className={`p-3 rounded-lg ${showAll ? 'mb-3' : ''}`}
              style={{
                background: `rgba(${currentTheme.accentRgb}, 0.05)`,
                border: `1px solid rgba(${currentTheme.accentRgb}, 0.2)`
              }}
            >
              {/* Header */}
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="w-5 h-5" style={{ color: currentTheme.accent }} />
                <div className="text-xs uppercase tracking-widest font-medium" style={{ color: currentTheme.secondary }}>
                  Prize Pool {challenge.max_winners > 1 && <span style={{ color: currentTheme.accent }}>• {challenge.max_winners} Winners</span>}
                </div>
              </div>
              
              {/* Prizes in a row */}
              <div className="flex items-center gap-4 flex-wrap">
                {/* 1st Place */}
                <div className="flex items-center gap-1.5">
                  <span className="text-lg">🥇</span>
                  <div 
                    className="text-2xl font-black"
                    style={{ 
                      color: currentTheme.accent,
                      textShadow: `0 0 15px rgba(${currentTheme.accentRgb}, 0.6)`
                    }}
                  >
                    {challenge.prize_1st_config?.prize_type === 'game_code' 
                      ? (challenge.prize_1st_config?.description || '🎮 Game Code')
                      : challenge.prize_1st_config?.prize_type === 'item' 
                        ? (challenge.prize_1st_config?.description || '🎁 Item')
                        : `$${challenge.prize_1st || challenge.prize_amount || 0}`}
                  </div>
                </div>

                {/* 2nd Place */}
                {(challenge.max_winners >= 2 || challenge.prize_2nd || challenge.prize_2nd_config) && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-base">🥈</span>
                    <div 
                      className="text-lg font-black"
                      style={{ color: '#c0c0c0' }}
                    >
                      {challenge.prize_2nd_config?.prize_type === 'game_code' 
                        ? (challenge.prize_2nd_config?.description || '🎮 Code')
                        : challenge.prize_2nd_config?.prize_type === 'item' 
                          ? (challenge.prize_2nd_config?.description || '🎁 Item')
                          : `$${challenge.prize_2nd || 0}`}
                    </div>
                  </div>
                )}

                {/* 3rd Place */}
                {(challenge.max_winners >= 3 || challenge.prize_3rd || challenge.prize_3rd_config) && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-base">🥉</span>
                    <div 
                      className="text-lg font-black"
                      style={{ color: '#cd7f32' }}
                    >
                      {challenge.prize_3rd_config?.prize_type === 'game_code' 
                        ? (challenge.prize_3rd_config?.description || '🎮 Code')
                        : challenge.prize_3rd_config?.prize_type === 'item' 
                          ? (challenge.prize_3rd_config?.description || '🎁 Item')
                          : `$${challenge.prize_3rd || 0}`}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Countdown Timer */}
          {showTimer && (
            <div 
              className="p-3 rounded-lg"
              style={{
                background: isLive ? `rgba(${currentTheme.accentRgb}, 0.03)` : 'rgba(255, 170, 0, 0.03)',
                border: `1px solid ${isLive ? `rgba(${currentTheme.accentRgb}, 0.15)` : 'rgba(255, 170, 0, 0.2)'}`
              }}
            >
              {timeLeft && !timeLeft.expired ? (
                <>
                  <div 
                    className="flex items-center gap-1.5 text-xs mb-2 uppercase tracking-widest" 
                    style={{ color: isLive ? currentTheme.secondary : '#ffaa00' }}
                  >
                    <Clock className="w-3 h-3" />
                    {timerLabel}
                  </div>
                  
                  <div className="flex items-baseline gap-1.5">
                    {timeLeft.days > 0 && (
                      <>
                        <div className="text-center">
                          <span className="text-3xl font-black font-mono" style={{ color: currentTheme.text }}>
                            {String(timeLeft.days).padStart(2, '0')}
                          </span>
                          <span className="text-xs ml-0.5 uppercase" style={{ color: currentTheme.secondary }}>d</span>
                        </div>
                        <span className="text-2xl font-bold" style={{ color: timerColor }}>:</span>
                      </>
                    )}
                    
                    <div className="text-center">
                      <span className="text-3xl font-black font-mono" style={{ color: currentTheme.text }}>
                        {String(timeLeft.hours).padStart(2, '0')}
                      </span>
                      <span className="text-xs ml-0.5 uppercase" style={{ color: currentTheme.secondary }}>h</span>
                    </div>
                    
                    <span className="text-2xl font-bold" style={{ color: timerColor }}>:</span>
                    
                    <div className="text-center">
                      <span className="text-3xl font-black font-mono" style={{ color: currentTheme.text }}>
                        {String(timeLeft.minutes).padStart(2, '0')}
                      </span>
                      <span className="text-xs ml-0.5 uppercase" style={{ color: currentTheme.secondary }}>m</span>
                    </div>
                    
                    <span className="text-2xl font-bold" style={{ color: timerColor }}>:</span>
                    
                    <div className="text-center">
                      <span 
                        className={`text-3xl font-black font-mono ${animate ? 'animate-pulse' : ''}`} 
                        style={{ color: timerColor }}
                      >
                        {String(timeLeft.seconds).padStart(2, '0')}
                      </span>
                      <span className="text-xs ml-0.5 uppercase" style={{ color: currentTheme.secondary }}>s</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-2">
                  <div 
                    className="flex items-center justify-center gap-1.5 text-xs mb-1 uppercase tracking-widest" 
                    style={{ color: '#ff6666' }}
                  >
                    <Clock className="w-3 h-3" />
                    {challenge.status === 'ended' ? 'CHALLENGE ENDED' : 'NO ACTIVE TIMER'}
                  </div>
                  <div className="text-lg font-bold" style={{ color: currentTheme.text }}>
                    {challenge.title}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Bounties Section - Between timer and branding */}
          {challenge.bounties && challenge.bounties.length > 0 && showAll && (
            <div 
              className="mt-3 p-3 rounded-lg"
              style={{
                background: 'linear-gradient(135deg, rgba(255,170,0,0.1), rgba(255,100,0,0.05))',
                border: '1px solid rgba(255, 170, 0, 0.3)',
              }}
            >
              <div 
                className="flex items-center gap-2 text-xs mb-3 uppercase tracking-widest font-bold" 
                style={{ color: '#ffaa00' }}
              >
                <Trophy className="w-4 h-4" />
                Bonus Bounties
              </div>
              
              <div className="space-y-2">
                {challenge.bounties.map((bounty, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <span className="text-base">⭐</span>
                    <div className="flex-1">
                      <p className="text-xs font-bold" style={{ color: '#ffcc00' }}>
                        {bounty.prize}
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.6)' }}>
                        └─ {bounty.rule}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Branding */}
          {showAll && (
            <div className="mt-auto pt-2 text-center text-xs" style={{ color: currentTheme.secondary }}>
              cesarmchallenges.com
            </div>
          )}
        </div>

        {/* Right Column - Rules (LIVE and SCHEDULED) */}
        {showAll && (isLive || isScheduled) && rules.length > 0 && (
          <div 
            className="w-[280px] flex-shrink-0 flex flex-col"
            style={{
              background: isLive ? 'rgba(255, 68, 68, 0.04)' : 'rgba(255, 170, 0, 0.04)',
              borderLeft: `1px solid ${isLive ? 'rgba(255, 68, 68, 0.2)' : 'rgba(255, 170, 0, 0.2)'}`
            }}
          >
            {/* Rules Header */}
            <div 
              className="p-4 text-sm uppercase tracking-widest font-bold flex-shrink-0"
              style={{ 
                color: isLive ? '#ff6666' : '#ffaa00',
                borderBottom: `1px solid ${isLive ? 'rgba(255, 68, 68, 0.15)' : 'rgba(255, 170, 0, 0.15)'}`,
                background: isLive ? 'rgba(255, 68, 68, 0.05)' : 'rgba(255, 170, 0, 0.05)'
              }}
            >
              Challenge Rules ({rules.length})
              {hasHiddenRules && <span className="text-xs ml-1 opacity-60">+more when live</span>}
            </div>
            
            {/* Rules List */}
            <div 
              className="flex-1 overflow-hidden relative"
              ref={rulesContainerRef}
            >
              <div 
                className="p-4"
                style={{
                  animation: needsScroll ? 'scrollRules 40s linear infinite' : 'none'
                }}
              >
                <ul className="space-y-3" ref={rulesListRef}>
                  {rules.map((rule, i) => (
                    <li 
                      key={i} 
                      className="flex items-start gap-3 text-sm"
                      style={{ 
                        color: 'rgba(255,255,255,0.85)',
                        filter: rule.shouldBlur ? 'blur(6px)' : 'none',
                        userSelect: rule.shouldBlur ? 'none' : 'auto'
                      }}
                    >
                      <span 
                        className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                        style={{ 
                          background: isLive ? `rgba(${currentTheme.accentRgb}, 0.2)` : 'rgba(255, 170, 0, 0.2)',
                          color: isLive ? currentTheme.accent : '#ffaa00'
                        }}
                      >
                        {i + 1}
                      </span>
                      <span 
                        className="leading-relaxed flex-1"
                        style={{ wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word' }}
                      >
                        {rule.text}
                      </span>
                    </li>
                  ))}
                </ul>
                
                {/* Duplicate for seamless scroll - for both LIVE and SCHEDULED when overflow */}
                {needsScroll && (
                  <ul className="space-y-3 mt-6">
                    {rules.map((rule, i) => (
                      <li 
                        key={`dup-${i}`} 
                        className="flex items-start gap-3 text-sm"
                        style={{ 
                          color: 'rgba(255,255,255,0.9)',
                          filter: rule.shouldBlur ? 'blur(6px)' : 'none',
                          userSelect: rule.shouldBlur ? 'none' : 'auto'
                        }}
                      >
                        <span 
                          className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                          style={{ 
                            background: isLive ? `rgba(${currentTheme.accentRgb}, 0.3)` : 'rgba(255, 170, 0, 0.3)',
                            color: isLive ? currentTheme.accent : '#ffaa00'
                          }}
                        >
                          {i + 1}
                        </span>
                        <span 
                          className="leading-relaxed flex-1"
                          style={{ wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word' }}
                        >
                          {rule.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      <style>{`
        @keyframes scrollRules {
          0% { transform: translateY(0); }
          100% { transform: translateY(-50%); }
        }
      `}</style>
    </div>
  );
};

export default OBSOverlay;
